import { useFormContext } from "react-hook-form";
import { useEffect, useMemo, useRef } from "react";

import { isManufacturedHomePark } from "@covertree/utils";
import type { InsuredTabFormValues } from "@redux/slices/endorsement";
import { HomeType, PolicyUsage } from "@covertree/new-business-graphql";
import { useEndorsementOutletContext } from "@layouts/EndorsementLayout";

/** Home Details form state, field visibility flags, and dependent-field sync. */
export const useHomeDetails = () => {
  const { policy } = useEndorsementOutletContext();

  const stateCode = policy?.units?.[0]?.address?.state;
  const currentYear = useRef(new Date().getFullYear());
  const isTenantFlow = policy?.mainUnitType === PolicyUsage.Tenant;

  const {
    control,
    formState: { errors },
    trigger,
    watch,
    setValue,
  } = useFormContext<InsuredTabFormValues>();

  const formRoofYearValue = watch("roofYearYyyy");
  const formLocationValue = watch("location");
  const formModelYearValue = watch("modelYear")?.value;
  const formHomeTypeValue = watch("homeType");
  const formManufacturerValue = watch("manufacturer");

  const triggerValidationOnConstructionYearValue = useMemo(
    () =>
      formRoofYearValue &&
      formModelYearValue &&
      formRoofYearValue >= formModelYearValue &&
      errors?.roofYearYyyy,
    [formRoofYearValue, errors, formModelYearValue]
  );

  const showParkNameSection = useMemo(
    () => isManufacturedHomePark(formLocationValue),
    [formLocationValue]
  );

  const showHomeHudAndSkirtingType = useMemo(
    () =>
      !isTenantFlow &&
      [
        HomeType.SingleWide,
        HomeType.DoubleWide,
        HomeType.TripleWide,
        HomeType.ParkModel,
        HomeType.Duplex,
        HomeType.CrossMod,
      ].includes(formHomeTypeValue),
    [isTenantFlow, formHomeTypeValue]
  );

  const showMakeCustom = useMemo(
    () => formManufacturerValue?.value === "Not Listed",
    [formManufacturerValue]
  );

  useEffect(() => {
    if (triggerValidationOnConstructionYearValue) {
      trigger("roofYearYyyy");
    }
  }, [triggerValidationOnConstructionYearValue, trigger]);

  useEffect(() => {
    const max =
      formModelYearValue && formModelYearValue > currentYear.current
        ? currentYear.current
        : formModelYearValue;
    if (formRoofYearValue && max && max > formRoofYearValue) {
      setValue("roofYearYyyy", undefined);
    }
  }, [formModelYearValue, formRoofYearValue, setValue]);

  useEffect(() => {
    if (formModelYearValue && !formRoofYearValue)
      setValue("roofYearYyyy", formModelYearValue);
  }, [formRoofYearValue, formModelYearValue, setValue]);

  useEffect(() => {
    if (!showHomeHudAndSkirtingType) {
      setValue("homeHudNumber", undefined);
      setValue("skirtingType", undefined);
    }
  }, [showHomeHudAndSkirtingType, setValue]);

  useEffect(() => {
    if (!showMakeCustom) {
      setValue("makeCustom", undefined);
    }
  }, [showMakeCustom, setValue]);

  return {
    control,
    errors,
    stateCode,
    isTenantFlow,
    showMakeCustom,
    formModelYearValue,
    showParkNameSection,
    showHomeHudAndSkirtingType,
  };
};
